// src/pages/wallet/HistoryPage.js
import React, { useEffect, useState} from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiSend, FiDownload, FiArrowDownLeft, FiDollarSign } from "react-icons/fi";
import { getWalletHistory } from "../../api/walletApi";
import { useAuth } from "../../hooks/useAuth";

function HistoryPage() {
  const { user } = useAuth();
  const userId = user?.user_id;

  const [transfers, setTransfers] = useState([]);
  const [withdraws, setWithdraws] = useState([]);
  const [receivedTransfers, setReceived] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("transfers"); // "transfers" | "withdraws" | "received"

  useEffect(() => {
    async function fetchHistory() {
      try {
        setLoading(true);
        setError("");

        if (!userId) {
          setError("User not found.");
          return;
        }

        const data = await getWalletHistory(userId);
        // Expected: { transferMoney: [], withdrawMoney: [] }
        setTransfers(data.transferMoney || []);
        setWithdraws(data.withdrawMoney || []);
        setReceived(data.receiveMoney|| []);
        setAdjustments(data.adjustments || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load wallet history.");
      } finally {
        setLoading(false);
      }
    }

    fetchHistory();
  }, [userId]);

  // const receivedTransfers = useMemo(
  //   () => transfers.filter((t) => t.direction === "incoming"),
  //   [transfers]
  // );

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return date.toLocaleString();
  };

  const formatAmount = (value) => {
    if (value == null) return "€ 0.00";
    return `€ ${Number(value).toFixed(2)}`;
  };

  return (
    <div className="space-y-6">
      {/* Back to wallet */}
      <div className="animate-fadeIn">
        <Link
          to="/wallet"
          className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
        >
          <FiArrowLeft className="mr-2 h-4 w-4" />
          Back to Wallet
        </Link>
      </div>

      <div className="animate-fadeIn">
        <h1 className="text-3xl font-bold text-gray-900">
          Wallet History
        </h1>
        <p className="text-gray-600 mt-1">
          View your transfer, received money, and withdraw history.
        </p>
      </div>

      {error && (
        <div className="card bg-gradient-to-br from-red-50 to-rose-50 border-red-200 animate-fadeIn">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Tabs */}
      <div className="card p-2 animate-slideUp">
        <div className="inline-flex w-full md:w-auto rounded-lg bg-gray-100 p-1 text-sm">
          <button
            type="button"
            onClick={() => setActiveTab("transfers")}
            className={
              "flex items-center gap-2 rounded-md px-4 py-2 transition-all " +
              (activeTab === "transfers"
                ? "bg-white text-primary-600 shadow-sm font-medium"
                : "text-gray-700 hover:text-gray-900")
            }
          >
            <FiSend className="h-4 w-4" />
            Transfers
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("received")}
            className={
              "flex items-center gap-2 rounded-md px-4 py-2 transition-all " +
              (activeTab === "received"
                ? "bg-white text-primary-600 shadow-sm font-medium"
                : "text-gray-700 hover:text-gray-900")
            }
          >
            <FiArrowDownLeft className="h-4 w-4" />
            Received
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("withdraws")}
            className={
              "flex items-center gap-2 rounded-md px-4 py-2 transition-all " +
              (activeTab === "withdraws"
                ? "bg-white text-primary-600 shadow-sm font-medium"
                : "text-gray-700 hover:text-gray-900")
            }
          >
            <FiDownload className="h-4 w-4" />
            Withdraws
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("adjustments")}
            className={
              "flex items-center gap-2 rounded-md px-4 py-2 transition-all " +
              (activeTab === "adjustments"
                ? "bg-white text-primary-600 shadow-sm font-medium"
                : "text-gray-700 hover:text-gray-900")
            }
          >
            <FiDollarSign className="h-4 w-4" />
            Adjustments
          </button>
        </div>
      </div>

      {loading ? (
        <div>Loading history...</div>
      ) : (
        <>
          {/* Transfers Tab */}
          {activeTab === "transfers" && (
            <div className="card p-0 overflow-hidden animate-fadeIn">
              <div className="border-b bg-gradient-to-r from-primary-50 to-secondary-50 px-6 py-4">
                <h3 className="font-semibold text-gray-900">Transfer History</h3>
              </div>
              {transfers.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  <FiSend className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No transfers found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600 font-medium">
                      <tr>
                        <th className="px-6 py-3">Date</th>
                        <th className="px-6 py-3">Direction</th>
                        <th className="px-6 py-3">Amount</th>
                        <th className="px-6 py-3">Fee</th>
                        <th className="px-6 py-3">To</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {transfers.map((t) => (
                        <tr key={t.id || t.transfer_money_id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-gray-600">
                            {formatDateTime(t.created_at)}
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={
                                "badge " +
                                (t.direction === "outgoing"
                                  ? "badge-error border border-red-200"
                                  : "badge-success border border-green-200")
                              }
                            >
                              {t.direction === "outgoing" ? "Sent" : "Received"}
                            </span>
                          </td>
                          <td className="px-6 py-4 font-semibold text-gray-900">
                            {formatAmount(t.amount)}
                          </td>
                          <td className="px-6 py-4 text-gray-600">
                            {formatAmount(t.cost || 0)}
                          </td>
                          <td className="px-6 py-4 text-gray-600">
                            {t.receiver_user_name != null ? t.receiver_user_name : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Received Money Tab */}
          {activeTab === "received" && (
            <div className="rounded-xl bg-white border shadow-sm overflow-hidden">
              <div className="border-b bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600">
                Received Money History
              </div>
              {receivedTransfers.length === 0 ? (
                <div className="px-4 py-4 text-xs text-slate-500">
                  No received money found.
                </div>
              ) : (
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Amount</th>
                      <th className="px-3 py-2">Cost</th>
                      <th className="px-3 py-2">From</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {receivedTransfers.map((t) => (
                      <tr key={t.id || t.transfer_money_id}>
                        <td className="px-3 py-2">
                          {formatDateTime(t.created_at)}
                        </td>
                        <td className="px-3 py-2 font-medium">
                          {formatAmount(t.amount)}
                        </td>
                        <td className="px-3 py-2">
                          {formatAmount(t.cost || 0)}
                        </td>
                        <td className="px-3 py-2">
                          {t.transferer_user_name != null ? t.transferer_user_name : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Withdraws Tab */}
          {activeTab === "withdraws" && (
            <div className="rounded-xl bg-white border shadow-sm overflow-hidden">
              <div className="border-b bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600">
                Withdraw History
              </div>
              {withdraws.length === 0 ? (
                <div className="px-4 py-4 text-xs text-slate-500">
                  No withdraws found.
                </div>
              ) : (
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Amount</th>
                      <th className="px-3 py-2">Cost</th>
                      <th className="px-3 py-2">Method</th>
                      <th className="px-3 py-2">Number</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {withdraws.map((w) => (
                      <tr key={w.id || w.withdraw_money_id}>
                        <td className="px-3 py-2">
                          {formatDateTime(w.created_at)}
                        </td>
                        <td className="px-3 py-2 font-medium">
                          {formatAmount(w.amount)}
                        </td>
                        <td className="px-3 py-2">
                          {formatAmount(w.cost || 0)}
                        </td>
                        <td className="px-3 py-2 uppercase">{w.method}</td>
                        <td className="px-3 py-2">{w.number}</td>
                        <td className="px-3 py-2">
                          <span
                            className={
                              "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] border " +
                              (w.status === "approved"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : w.status === "rejected"
                                ? "bg-red-50 text-red-700 border-red-100"
                                : "bg-amber-50 text-amber-700 border-amber-100")
                            }
                          >
                            {w.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === "adjustments" && (
            <div className="rounded-xl bg-white border shadow-sm overflow-hidden">
              <div className="border-b bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-600">
                Wallet Adjustments (Bonus / Penalty)
              </div>
              {adjustments.length === 0 ? (
                <div className="px-4 py-4 text-xs text-slate-500">
                  No adjustments found.
                </div>
              ) : (
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
                    <tr>
                      <th className="px-3 py-2">Date</th>
                      <th className="px-3 py-2">Type</th>
                      <th className="px-3 py-2">Amount</th>
                      <th className="px-3 py-2">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {adjustments.map((a) => {
                      const isBonus = a.type === "BONUS";
                      return (
                        <tr key={a.adjustment_id}>
                          <td className="px-3 py-2">
                            {formatDateTime(a.created_at)}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={
                                "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] border " +
                                (isBonus
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                  : "border-red-200 bg-red-50 text-red-700")
                              }
                            >
                              {isBonus ? "BONUS" : "PENALTY"}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-medium">
                            {isBonus ? "+" : "-"}
                            {formatAmount(a.amount)}
                          </td>
                          <td className="px-3 py-2 max-w-xs">
                            <span className="line-clamp-2">
                              {a.note || "-"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HistoryPage;
